'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Label } from '@components/ui/label';
import { Shuffle, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { AUTO_ADVANCE_CONFIG, PROGRESS_BAR_ANIMATION } from '@acme/lib';

// Define a constant for the shuffle value
export const RANDOM_VALUE = 'random';

interface ColorOption {
  value: string;
  name: string;
}

type Props = {
  /**
   * Array of color options to display
   */
  colors: ColorOption[];

  /**
   * Currently selected color or "shuffle"
   */
  value: string | undefined;

  /**
   * Function called when a color or "shuffle" is selected
   */
  onChange: (val: string) => void;

  /**
   * Optional label for the color selector
   */
  label?: string;

  /**
   * Whether to show a shuffle option as the first item
   */
  showShuffleOption?: boolean;

  /**
   * Whether to show hex value instead of color name
   */
  showHexValue?: boolean;

  /**
   * Display variant - either 'grid' or 'list'
   */
  variant?: 'grid' | 'list';

  /**
   * Auto-advance callback for survey questions
   */
  onAutoAdvance?: () => void;
};

/**
 * ColorSelectorV2 - An improved version of the ColorSelector component
 * with better TypeScript typing and handling of undefined values
 */
export default function ColorSelectorV2({
  colors,
  onChange,
  value,
  label = 'Select a color',
  showShuffleOption = false,
  showHexValue = false,
  variant = 'grid',
  onAutoAdvance
}: Props) {
  // Don't default to first color - let value be undefined/empty to force user selection
  const safeValue = value || '';

  // Animation state for auto-advance
  const [animatingValue, setAnimatingValue] = useState<string | null>(null);

  const onAutoAdvanceRef = useRef(onAutoAdvance);
  useEffect(() => {
    onAutoAdvanceRef.current = onAutoAdvance;
  }, [onAutoAdvance]);

  // Handle selection with animation
  const handleSelect = (colorValue: string) => {
    onChange(colorValue);
    setAnimatingValue(colorValue);

    if (onAutoAdvanceRef.current) {
      setTimeout(() => {
        setAnimatingValue(null);
        if (onAutoAdvanceRef.current) {
          onAutoAdvanceRef.current();
        }
      }, AUTO_ADVANCE_CONFIG.TOTAL_DELAY);
    } else {
      // Clear animation after it completes even without auto-advance
      setTimeout(() => {
        setAnimatingValue(null);
      }, AUTO_ADVANCE_CONFIG.ANIMATION_DURATION);
    }
  };

  // Check if shuffle is selected
  const isRandomSelected = safeValue === RANDOM_VALUE;

  // Find the selected color option
  const selectedColor = colors.find(color => color.value === safeValue);

  // Handle shuffle button click
  const handleRandomSelect = () => {
    onChange(RANDOM_VALUE);
  };

  const renderColorButton = (color: ColorOption, index: number) => {
    const isSelected = safeValue === color.value;
    const isNoneOption = color.value === 'none';
    const isAnimating = animatingValue === color.value;

    if (variant === 'list') {
      return (
        <motion.div
          key={index}
          className={cn(
            'group flex h-[50px] w-full hover:bg-accent/10 rounded-md border border-input bg-transparent px-3 text-base shadow-sm transition-colors relative cursor-pointer placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm items-center justify-between focus-visible:ring-offset-2 overflow-hidden',
            isSelected ? 'border-accent bg-accent/10' : 'border-muted bg-card hover:border-accent',
            'hover:bg-accent/5 hover:border-accent/50'
          )}
          onClick={() => handleSelect(color.value)}
          whileTap={{ scale: 0.98 }}
          animate={
            isAnimating
              ? {
                  opacity: AUTO_ADVANCE_CONFIG.ANIMATION_PATTERN
                }
              : {}
          }
          transition={{
            duration: AUTO_ADVANCE_CONFIG.ANIMATION_DURATION / 1000,
            ease: AUTO_ADVANCE_CONFIG.ANIMATION_EASING
          }}
          tabIndex={0}
          role="radio"
          aria-checked={isSelected}
        >
          {/* Progress bar background */}
          {isAnimating && (
            <motion.div
              className="absolute inset-0 origin-left"
              style={{
                backgroundColor: AUTO_ADVANCE_CONFIG.PROGRESS_BAR_COLOR,
                opacity: AUTO_ADVANCE_CONFIG.PROGRESS_BAR_OPACITY
              }}
              initial={PROGRESS_BAR_ANIMATION.initial}
              animate={PROGRESS_BAR_ANIMATION.animate}
              transition={PROGRESS_BAR_ANIMATION.transition}
            />
          )}

          <div className="flex items-center gap-3 relative z-10">
            {isNoneOption ? (
              <div className="w-5 h-5 rounded-full ring-[1px] ring-inset ring-primary bg-muted relative flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 rounded-full border-2 border-muted-foreground"></div>
                <div className="absolute w-[2px] h-6 bg-muted-foreground rotate-45 transform origin-center"></div>
              </div>
            ) : (
              <div
                className="w-5 h-5 rounded-full dark:ring-[1px] dark:ring-white border-[1px] border-primary/10"
                style={{ backgroundColor: color.value }}
              />
            )}
            <span className="text-sm capitalize">{showHexValue && !isNoneOption ? color.value : color.name}</span>
          </div>
          <motion.div
            className={cn(
              'relative z-10 flex h-5 w-5 items-center justify-center rounded-full border group-hover:bg-accent group-hover:border-accent',
              isSelected ? 'border-accent bg-accent' : 'bg-muted/80 border'
            )}
            animate={
              isAnimating
                ? {
                    opacity: AUTO_ADVANCE_CONFIG.ANIMATION_PATTERN
                  }
                : {}
            }
            transition={{
              duration: AUTO_ADVANCE_CONFIG.ANIMATION_DURATION / 1000,
              ease: AUTO_ADVANCE_CONFIG.ANIMATION_EASING
            }}
          >
            {isSelected && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
                <Check strokeWidth={3} className="h-3 w-3 text-background" />
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      );
    }

    return (
      <button
        key={index}
        onClick={() => handleSelect(color.value)}
        className={cn(
          'w-12 h-auto aspect-square rounded-full relative',
          isSelected
            ? 'ring-2 ring-accent ring-offset-2 ring-offset-background'
            : 'hover:opacity-80 ring-[1px] ring-inset ring-primary/10',
          isNoneOption && 'bg-muted flex items-center justify-center overflow-hidden'
        )}
        style={!isNoneOption ? { backgroundColor: color.value } : {}}
        aria-label={`Select color ${color.name}`}
        type="button"
      >
        {isNoneOption && (
          <>
            <div className="absolute inset-0 rounded-full border border-muted-foreground/60"></div>
            <div className="absolute w-[1px] h-12 bg-muted-foreground/60 rotate-45 transform origin-center"></div>
          </>
        )}
      </button>
    );
  };

  const renderShuffleButton = () => {
    if (!showShuffleOption) return null;

    const isAnimating = animatingValue === RANDOM_VALUE;

    if (variant === 'list') {
      return (
        <motion.div
          className={cn(
            'group flex h-[50px] w-full hover:bg-accent/10 rounded-md border border-input bg-transparent px-3 text-base shadow-sm transition-colors relative cursor-pointer placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm items-center justify-between focus-visible:ring-offset-2 overflow-hidden',
            isRandomSelected ? 'border-accent bg-accent/10' : 'border-muted bg-card hover:border-accent',
            'hover:bg-accent/5 hover:border-accent/50'
          )}
          onClick={() => handleSelect(RANDOM_VALUE)}
          whileTap={{ scale: 0.98 }}
          animate={
            isAnimating
              ? {
                  opacity: AUTO_ADVANCE_CONFIG.ANIMATION_PATTERN
                }
              : {}
          }
          transition={{
            duration: AUTO_ADVANCE_CONFIG.ANIMATION_DURATION / 1000,
            ease: AUTO_ADVANCE_CONFIG.ANIMATION_EASING
          }}
          tabIndex={0}
          role="radio"
          aria-checked={isRandomSelected}
        >
          {/* Progress bar background */}
          {isAnimating && (
            <motion.div
              className="absolute inset-0 origin-left"
              style={{
                backgroundColor: AUTO_ADVANCE_CONFIG.PROGRESS_BAR_COLOR,
                opacity: AUTO_ADVANCE_CONFIG.PROGRESS_BAR_OPACITY
              }}
              initial={PROGRESS_BAR_ANIMATION.initial}
              animate={PROGRESS_BAR_ANIMATION.animate}
              transition={PROGRESS_BAR_ANIMATION.transition}
            />
          )}

          <div className="flex items-center gap-3 relative z-10">
            <div className="w-5 h-5 rounded-full bg-foreground/10 flex items-center justify-center">
              <Shuffle className="h-3 w-3 text-foreground/70" />
            </div>
            <span className="text-sm">Random</span>
          </div>
          <motion.div
            className={cn(
              'relative z-10 flex h-5 w-5 items-center justify-center rounded-full border group-hover:bg-accent group-hover:border-accent',
              isRandomSelected ? 'border-accent bg-accent' : 'bg-muted/80 border'
            )}
            animate={
              isAnimating
                ? {
                    opacity: AUTO_ADVANCE_CONFIG.ANIMATION_PATTERN
                  }
                : {}
            }
            transition={{
              duration: AUTO_ADVANCE_CONFIG.ANIMATION_DURATION / 1000,
              ease: AUTO_ADVANCE_CONFIG.ANIMATION_EASING
            }}
          >
            {isRandomSelected && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
                <Check strokeWidth={3} className="h-3 w-3 text-background" />
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      );
    }

    return (
      <button
        onClick={handleRandomSelect}
        className={cn(
          'w-12 rounded-full h-auto aspect-square flex justify-center items-center bg-foreground/10',
          isRandomSelected
            ? 'ring-2 ring-accent ring-offset-2 ring-offset-background'
            : 'hover:bg-foreground/20 ring-[1px] ring-inset ring-primary/10'
        )}
        aria-label="Select random option"
        type="button"
      >
        <Shuffle className="h-4 w-4 sm:h-5 sm:w-5 text-foreground/70" />
      </button>
    );
  };

  return (
    <div className="w-full mx-auto space-y-4">
      <div className="flex flex-col items-start">
        {label && (
          <Label htmlFor="color-selector" className="text-current">
            {label}
          </Label>
        )}
        {label && safeValue && (
          <div className="flex justify-center items-center gap-1">
            {isRandomSelected ? (
              <div className="flex items-center gap-1">
                <div className="flex-shrink-0 w-3 h-3 rounded-md ring-[1px] ring-inset ring-primary/10 items-center justify-center flex">
                  <Shuffle size={8} className="text-foreground/70" />
                </div>
                <div className="text-base text-current text-right capitalize">Random</div>
              </div>
            ) : selectedColor?.value === 'none' ? (
              <div className="flex items-center gap-1">
                <div className="flex-shrink-0 w-3 h-3 rounded-full ring-[1px] ring-inset ring-primary/10 bg-muted relative overflow-hidden">
                  <div className="absolute inset-0 rounded-full border border-foreground/70"></div>
                  <div className="absolute w-[1px] h-4 bg-foreground/70 rotate-45 transform origin-center top-[-2px] left-[50%] translate-x-[-50%]"></div>
                </div>
                <div className="text-base text-current text-right capitalize">{selectedColor.name}</div>
              </div>
            ) : (
              selectedColor && (
                <>
                  <div
                    className="flex-shrink-0 w-3 h-3 rounded-md ring-[1px] ring-inset ring-primary/10"
                    style={{ backgroundColor: selectedColor.value }}
                  />
                  <div className="text-base text-current text-right capitalize">
                    {showHexValue ? selectedColor.value : selectedColor.name}
                  </div>
                </>
              )
            )}
          </div>
        )}
      </div>

      {variant === 'list' ? (
        <div className="w-full space-y-4">
          {renderShuffleButton()}
          {colors.map(renderColorButton)}
        </div>
      ) : (
        <div className={cn('w-full flex flex-wrap gap-2')}>
          {renderShuffleButton()}
          {colors.map(renderColorButton)}
        </div>
      )}
    </div>
  );
}
